import React, { useRef, useEffect, useState } from 'react'
import { AircraftOverride } from '../../../types/database'
import { ExportOptions } from '../ExportOptionsModal'
import { getEffectiveAircraftData } from '../../../utils/overrides'
import { MarketTableData } from './types'

interface HideAircraftDropdownProps {
  tables: MarketTableData[]
  overrides: Map<number, AircraftOverride>
  exportOptions: ExportOptions
  hiddenAircraftIds: Set<number>
  onHiddenAircraftChange: (updater: (prev: Set<number>) => Set<number>) => void
  getVisibleTables: (tables: MarketTableData[]) => MarketTableData[]
}

export default function HideAircraftDropdown({
  tables,
  overrides,
  exportOptions,
  hiddenAircraftIds,
  onHiddenAircraftChange,
  getVisibleTables
}: HideAircraftDropdownProps) {
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  const visibleTables = getVisibleTables(tables)

  const allAircraft: Array<{
    aircraftId: number
    serialNumber: string
    section: 'for-sale' | 'sold' | 'removed'
    isOffMarket: boolean
    sortDate: string
  }> = []

  visibleTables.forEach(table => {
    table.data.forEach(d => {
      const isOffMarket = table.type === 'for-sale' && (
        d.source === 'offMarket' || (() => {
          const override = overrides.get(d.aircraft.aircraft_id)
          const effectiveAircraft = getEffectiveAircraftData(d.aircraft, override)
          return effectiveAircraft.status === 'Off Market'
        })()
      )

      if (exportOptions.hideOffMarket && isOffMarket) return

      let sortDate = '1900-01-01'
      if (table.type === 'sold') {
        sortDate = d.aircraftTransaction?.transaction_date || d.transaction?.trans_date || '1900-01-01'
      } else if (table.type === 'removed') {
        sortDate = d.event?.date || '1900-01-01'
      }

      allAircraft.push({
        aircraftId: d.aircraft.aircraft_id,
        serialNumber: d.aircraft.sernbr || `ID: ${d.aircraft.aircraft_id}`,
        section: table.type,
        isOffMarket,
        sortDate
      })
    })
  })

  const sectionOrder = { 'for-sale': 0, 'sold': 1, 'removed': 2 }
  allAircraft.sort((a, b) => {
    const sectionDiff = sectionOrder[a.section] - sectionOrder[b.section]
    if (sectionDiff !== 0) return sectionDiff
    if (a.section === 'sold' || a.section === 'removed') {
      return b.sortDate.localeCompare(a.sortDate)
    }
    return a.serialNumber.localeCompare(b.serialNumber, undefined, { numeric: true })
  })

  if (allAircraft.length === 0) return null

  const getSectionLabel = (section: 'for-sale' | 'sold' | 'removed') => {
    switch (section) {
      case 'for-sale': return 'For Sale'
      case 'sold': return 'Sold'
      case 'removed': return 'Removed'
    }
  }

  const getSectionBadgeClass = (section: 'for-sale' | 'sold' | 'removed') => {
    switch (section) {
      case 'for-sale': return 'bg-green-100 text-green-700'
      case 'sold': return 'bg-blue-100 text-blue-700'
      case 'removed': return 'bg-gray-100 text-gray-600'
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
      >
        <span>Hide Aircraft</span>
        {hiddenAircraftIds.size > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
            {hiddenAircraftIds.size}
          </span>
        )}
        <svg className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {showDropdown && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          <div className="p-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500">Select aircraft to include in PDF</span>
          </div>
          {allAircraft.map(({ aircraftId, serialNumber, section, isOffMarket }) => (
            <label
              key={`${section}-${aircraftId}`}
              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={!hiddenAircraftIds.has(aircraftId)}
                onChange={(e) => {
                  onHiddenAircraftChange(prev => {
                    const next = new Set(prev)
                    if (e.target.checked) {
                      next.delete(aircraftId)
                    } else {
                      next.add(aircraftId)
                    }
                    return next
                  })
                }}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700 flex-1">{serialNumber}</span>
              <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${getSectionBadgeClass(section)}`}>
                {getSectionLabel(section)}
              </span>
              {isOffMarket && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-600 rounded">
                  Off Mkt
                </span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

